import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logout } from '../store/slices/authSlice';
import { fetchNewContactsCountAsync } from '../store/slices/contactsSlice';
import './Sidebar.css';

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  active: boolean;
  badge?: string;
  notificationCount?: number;
}

interface MenuSection {
  id: string;
  title?: string;
  items: MenuItem[];
}

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const newContactsCount = useAppSelector((state) => state.contacts.newContactsCount);
  const navRef = useRef<HTMLElement>(null);
  const contactsRef = useRef<HTMLLIElement>(null);
  const [contactsBadgeVisible, setContactsBadgeVisible] = useState(true);

  useEffect(() => {
    dispatch(fetchNewContactsCountAsync());
    const interval = setInterval(() => dispatch(fetchNewContactsCountAsync()), 60000);
    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    const el = contactsRef.current;
    const root = navRef.current;
    if (!el || !root) return;

    const observer = new IntersectionObserver(
      ([entry]) => setContactsBadgeVisible(entry.isIntersecting),
      { root, threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToContacts = useCallback(() => {
    contactsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const menuSections: MenuSection[] = [
    {
      id: 'overview',
      items: [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: '📊',
          path: '/dashboard',
          active: location.pathname === '/dashboard'
        },
        {
          id: 'stats',
          label: 'Stats',
          icon: '📈',
          path: '/stats',
          active: location.pathname.startsWith('/stats')
        }
      ]
    },
    {
      id: 'academics',
      title: 'Teaching',
      items: [
        {
          id: 'groups',
          label: 'Groups',
          icon: '👥',
          path: '/groups',
          active: location.pathname.startsWith('/groups')
        },
        {
          id: 'students',
          label: 'Students',
          icon: '🎓',
          path: '/students',
          active: location.pathname.startsWith('/students')
        },
        {
          id: 'lessons',
          label: 'Lessons',
          icon: '📚',
          path: '/lessons',
          active: location.pathname.startsWith('/lessons')
        },
        {
          id: 'journal',
          label: 'Journal',
          icon: '📋',
          path: '/journal',
          active: location.pathname.startsWith('/journal')
        },
        {
          id: 'courses',
          label: 'Courses',
          icon: '🏫',
          path: '/courses',
          active: location.pathname.startsWith('/courses')
        },
        {
          id: 'vocabulary',
          label: 'Vocabulary',
          icon: '📝',
          path: '/vocabulary',
          active: location.pathname.startsWith('/vocabulary')
        }
      ]
    },
    {
      id: 'attendance',
      title: 'Attendance',
      items: [
        {
          id: 'absences',
          label: 'Absences',
          icon: '📉',
          path: '/absences',
          active: location.pathname.startsWith('/absences')
        },
        {
          id: 'vacations',
          label: 'Vacations',
          icon: '🏖️',
          path: '/vacations',
          active: location.pathname.startsWith('/vacations')
        },
        {
          id: 'student-vacations',
          label: 'Student vacations',
          icon: '🧳',
          path: '/student-vacations',
          active: location.pathname.startsWith('/student-vacations')
        }
      ]
    },
    {
      id: 'finance',
      title: 'Finance',
      items: [
        {
          id: 'subscriptions',
          label: 'Subscriptions',
          icon: '💳',
          path: '/subscriptions',
          active: location.pathname.startsWith('/subscriptions'),
          badge: 'Beta'
        },
        {
          id: 'expiring-subscriptions',
          label: 'Expiring',
          icon: '⏰',
          path: '/expiring-subscriptions',
          active: location.pathname.startsWith('/expiring-subscriptions')
        },
        {
          id: 'accounting',
          label: 'Accounting',
          icon: '💰',
          path: '/accounting',
          active: location.pathname.startsWith('/accounting') && !location.pathname.startsWith('/accounting/stats')
        },
        {
          id: 'accounting-stats',
          label: 'Statistics',
          icon: '🧮',
          path: '/accounting/stats',
          active: location.pathname.startsWith('/accounting/stats')
        }
      ]
    },
    {
      id: 'marketing',
      title: 'Marketing',
      items: [
        {
          id: 'ads',
          label: 'Ads',
          icon: '📢',
          path: '/ads',
          active: location.pathname.startsWith('/ads')
        },
        {
          id: 'consultations',
          label: 'Consultations',
          icon: '💬',
          path: '/consultations',
          active: location.pathname.startsWith('/consultations')
        },
        {
          id: 'giveaways',
          label: 'Giveaways',
          icon: '🎁',
          path: '/giveaways',
          active: location.pathname.startsWith('/giveaways')
        },
        {
          id: 'mentions',
          label: 'Mentions',
          icon: '📸',
          path: '/mentions',
          active: location.pathname.startsWith('/mentions')
        },
        {
          id: 'contacts',
          label: 'Contacts',
          icon: '📋',
          path: '/contacts',
          active: location.pathname.startsWith('/contacts'),
          notificationCount: newContactsCount
        },
        {
          id: 'reels-guide',
          label: 'Reels guide',
          icon: '🎬',
          path: '/marketing/reels',
          active: location.pathname.startsWith('/marketing/reels')
        }
      ]
    },
    {
      id: 'system',
      title: 'System',
      items: [
        {
          id: 'settings',
          label: 'Settings',
          icon: '⚙️',
          path: '/settings',
          active: location.pathname === '/settings'
        }
      ]
    }
  ];

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <div
          className="sidebar-logo"
          onClick={() => navigate('/dashboard')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/dashboard'); }}
          style={{ cursor: 'pointer' }}
        >
          <h2>Clara Admin</h2>
        </div>
      </div>

      <nav className="sidebar-nav" ref={navRef}>
        {menuSections.map((section) => (
          <React.Fragment key={section.id}>
            {section.title && <div className="sidebar-section-divider"></div>}
            {section.title && <div className="sidebar-section-header">{section.title}</div>}
            <ul className="sidebar-menu">
              {section.items.map((item) => (
                <li
                  key={item.id}
                  ref={item.id === 'contacts' ? contactsRef : undefined}
                  className={`sidebar-menu-item ${item.active ? 'active' : ''}`}
                >
                  <Link
                    to={item.path}
                    className="sidebar-menu-button"
                  >
                    <span className="sidebar-menu-icon">{item.icon}</span>
                    <span className="sidebar-menu-label">{item.label}</span>
                    {item.badge && (
                      <span className="sidebar-menu-badge">{item.badge}</span>
                    )}
                    {(item.notificationCount ?? 0) > 0 && (
                      <span className="sidebar-notification-dot">{item.notificationCount}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </React.Fragment>
        ))}

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="sidebar-logout-button">
            <span className="sidebar-menu-icon">🚪</span>
            <span>Logout</span>
          </button>
        </div>

        {newContactsCount > 0 && !contactsBadgeVisible && (
          <button className="sidebar-scroll-indicator" onClick={scrollToContacts}>
            <span className="sidebar-scroll-indicator-dot">{newContactsCount}</span>
            <span className="sidebar-scroll-indicator-arrow">&#x25BC;</span>
          </button>
        )}
      </nav>
    </div>
  );
};

export default Sidebar;
